import { CFade } from "@coreui/react";
import { FC, createContext, useEffect, useState } from "react";
import { constants } from "script/constant";
import api from "services/api.service";
import commonApi from "services/apis/common.api";
import cookieService from "services/cookie.service";
import Loader from "views/widgets/loader";

export interface IAuth {
  user: IAuthUser;
  plugins: IPlugin[];
  appPermission: IAppPermission;
  autoSyncDeadlineExist: boolean;
  userSettings: IUserSetting;
  onLogin: () => void;
  onLogout: () => void;
  onSignup: () => void;
  getUserSettings: () => void;
  isAuthenticated: boolean;
  isAIEnabled: boolean;
  isBetaAIEnabled: boolean;
  isCreateMatterEnabled: boolean;
  syncInitiatedUser: ISyncInitiatedUser;
  designatedUserData: {
    isDesignatedUserValid: boolean;
    setDesignatedUserStatus: React.Dispatch<React.SetStateAction<boolean>>;
  }
  // infoTrackBaseUrl: string;
}

const authContentDefaults: IAuth = {
  user: {
    botConversationReference: null,
    hasAdminPortalAccess: false,
    isPrivateUser: false,
    sharePointSiteName: null,
    isGroupLinkEnabled: false,
    isAttachGroupEnabled: false,
    isCreateGroupEnabled: false,
    office365_TenantID: null,
    office365_UserObjectID: null,
    office365_UserUniqueID: null,
    isDeleted: false,
    isActive: false,
    isNew: false,
    userPrefTimeZone: null,
    userType: null,
    logo: null,
    channelID: 0,
    appID: 0,
    payStatus: 0,
    firmEmail: null,
    firmName: "",
    firmID: 0,
    userImage: null,
    userUPN: null,
    userEmail: "",
    userName: "",
    userID: 0,
    caseRename: null,
    isDocketingUser: false,
    stateAbr: null,
    userTimeZoneName: "",
    stateName: "",
    syncInitiatedUserID: 0,
  },
  plugins: [],
  userSettings: {
    isPrivateUser: false,
    hasAdminPortalAccess: false,
    hasThirdPartyDoc: false,
    privateUserCount: 0,
    isEtickleReminder: false,
    canCreateMatter: true,
    canAddMiscAppointment: false,
    canCloseMatter: false,
    canSaveEmail: false,
    canCalculateDeadlines: false,
    canViewFirmMatters: false,
    canEditDeadline: false,
    canDeleteDeadline: false,
    canShareCalendarExternally: false,
    canViewDocumentFolder: false,
    canViewSharedInbox: false,
    canViewOneNotes: false,
    canViewCalendar: false,
    createMatterValidation: false,
    documentFolderUrl: "",
    canViewContacts: false,
    canAddContacts: false,
    canCopyTemplatesInTeams: false,
    canCopyTemplates: false,
    canCopyTemplatesInOutlook: false,
    canAddContactTags: false,
    canCreateVirtualMeetings: false,
    firmCanCreateVirtualMeetings: false,
    canManageContacts: false,
    canSaveEmailInGroup: false,
    isSharepointSaveEmail: false,
    canCreateTeam: false,
    canCreateTeamWithMatter: false,
    matterFilesPreference: false,
    addPrefixSuffixToGroupName: false,
    addPrefixSuffixToGroupEmail: false,
    addPrefixSuffixToLTBMatterName: false,
    isGroupOwner: false,
    canShareFile: false,
    canViewTimeSheets: false,
    allowCentralDocketingUserAsGroupAdmin: false,
    allowFirmUsersToCreateMSTeam: false,
    showDelveLink: false,
    autoDeleteCancelledEvent: false,
    isLongDescriptionDeadlineTypeEnabled: false,
    isPlannerEnabled: false,
    canViewSwitchMatterCard: false,
    canViewUploadDocument: false,
    canViewSaveEmail: false,
    canViewEmails: false,
    canViewMatterContacts: false,
    canCreateReminder: false,
    canSelectTeamsTemplate: true,
    calendarReminder: false,
    eventEmailReminder: false,
    showAddNoSync: true
  },
  appPermission: {
    isMSGraphAppOnlyConsented: true,
    isMSGraphAppOnlyEnabled: true,
    isAdminConsented: true,
    allowSetUserInAdmin: false,
    applicationPermissionEnabled: true
  },
  syncInitiatedUser: {
    userID: 0,
    userName: "",
    userEmail: "",
  },
  autoSyncDeadlineExist: false,
  onLogin: () => { },
  onLogout: () => { },
  onSignup: () => { },
  getUserSettings: () => { },
  isAuthenticated: false,
  isAIEnabled: true,
  isBetaAIEnabled: false,
  isCreateMatterEnabled: true,
  designatedUserData: {
    isDesignatedUserValid: true,
    setDesignatedUserStatus: (() => { }) as React.Dispatch<React.SetStateAction<boolean>>
  }
  //  infoTrackBaseUrl: "",
};

export const authContext = createContext<IAuth>(authContentDefaults);

const AuthProvider: FC = ({ children }) => {
  const [user, setUser] = useState<IAuthUser>(authContentDefaults.user);
  const [syncInitiatedUser] = useState<any>(authContentDefaults.syncInitiatedUser);
  const [userSettings, setSettings] = useState<IUserSetting>(authContentDefaults.userSettings);
  const [plugins, setPlugins] = useState<IPlugin[]>(authContentDefaults.plugins);
  // const [infoTrackBaseUrl, setInfoTrackBaseUrl] = useState("");
  const [isLoading, setLoader] = useState<boolean>(true);
  const [isAuthenticated, setAuthentication] = useState<boolean>(
    authContentDefaults.isAuthenticated
  );
  const [appPermission, setPermission] = useState<IAppPermission>(authContentDefaults.appPermission);
  const [autoSyncDeadlineExist, setAutoSyncDeadlineExist] = useState<boolean>(authContentDefaults.autoSyncDeadlineExist);
  const [isAIEnabled, setAIEnabled] = useState<boolean>(authContentDefaults.isAIEnabled);
  const [isBetaAIEnabled, setBetaAIEnabled] = useState<boolean>(authContentDefaults.isAIEnabled);
  const [isCreateMatterEnabled, setCreateMatterEnabled] = useState<boolean>(authContentDefaults.isCreateMatterEnabled);
  const [isDesignatedUserValid, setDesignatedUserStatus] = useState<boolean>(true);

  useEffect(() => {
    fetchApiandPlugins();
  }, []);

  const fetchApiandPlugins = () => {
    setLoader(true);
    try {
      getSettings().then((res) => {
        if (res) {
          getAppConsent();
          getAutoSyncDeadline();
          fetchInitialApis();
          loadPlugin();
          // checkDesignatedUser();
        }
      });

    } catch (error) { }
  }
  const checkDesignatedUser = () => {
    api
      .get(constants.CHECK_DESIGNATED_USER)
      .then((res) => {
        setDesignatedUserStatus(res.data);
      })
      .catch(err => {
        setDesignatedUserStatus(false)
      });
  }
  const getAppConsent = () => {
    api
      .get(constants.GET_APP_PERMISSION)
      .then((response) => {
        setPermission(response.data);
      }).catch((error: XMLHttpRequest) => {
        if (error.status === 401) {
          setAuthentication(false);
        }
        setLoader(false);
      })
  }

  const getAutoSyncDeadline = async () => {
    api
      .get(constants.CHECK_AUTO_SYNC_DEADLINE)
      .then((response) => {
        setAutoSyncDeadlineExist(response.data);
      }).catch((error: XMLHttpRequest) => {
        if (error.status === 401) {
          setAuthentication(false);
        }
        setLoader(false);
      })
  }
  const getSettings = async () => {
    return await commonApi.fetchSettings().then((res) => {
      setSettings(res.firmSettings);
      setAIEnabled(res.isAIEnabled);
      setBetaAIEnabled(res.isBetaAIEnabled);
      setCreateMatterEnabled(res.isCreateMatterEnabled);
      return res;
    }).catch((error: XMLHttpRequest) => {
      if (error.status === 401) {
        setAuthentication(false);
      }
      setLoader(false);
    })
  }

  const getUserHandler = async () => {
    await commonApi
      .fetchUser()
      .then((res) => {
        setAuthentication(true);
        setUser(res);
        // if (res.syncInitiatedUserID != 0) {
        //   axios.post(constants.USERS_EDIT_URI + "/" + res.syncInitiatedUserID).then((response) => {
        //     const values = {
        //       userName: response.data.coreUser.name,
        //       userID: response.data.coreUser.userID,
        //       userEmail: response.data.coreUser.email,
        //     };
        //     setSyncInitiatedUser(values)
        //   })
        // }
      })
      .catch((error: XMLHttpRequest) => {
        if (error.status === 401) {
          setAuthentication(false);
        }
      })
      .finally(() => {
        setLoader(false);
      });
  }

  const fetchInitialApis = () => {
    getUserHandler();
  };

  const loginHandler = () => fetchApiandPlugins();

  const logoutHandler = () => {
    cookieService.delete("token");
    setAuthentication(false);
    setUser(authContentDefaults.user);
  };

  const signupHandler = () => {
    fetchInitialApis()
  }

  const loadPlugin = async () => {
    commonApi.fetchPlugins().then((res) => {
      setPlugins(res);
      //  setInfoTrackBaseUrl(res.infoTrackBaseUrl);
    }).catch((error: XMLHttpRequest) => {
      if (error.status === 401) {
        setAuthentication(false);
      }
    })
  };

  return (
    <authContext.Provider
      value={{
        user,
        onLogin: loginHandler,
        onLogout: logoutHandler,
        onSignup: signupHandler,
        getUserSettings: getSettings,
        userSettings,
        isAuthenticated,
        isAIEnabled,
        isBetaAIEnabled,
        appPermission,
        isCreateMatterEnabled,
        autoSyncDeadlineExist,
        plugins,
        syncInitiatedUser,
        designatedUserData: {
          isDesignatedUserValid,
          setDesignatedUserStatus
        }
        //  infoTrackBaseUrl,
      }}
    >
      {isLoading ? (
        <div className="ltb-loader-page">
          <Loader></Loader>
        </div>
      ) : (
        <CFade>{children}</CFade>
      )}
    </authContext.Provider>
  );
};
export default AuthProvider;